// @ts-strict-ignore
import {Component, computed, inject, OnInit, signal} from "@angular/core";
import {FetchDataType, Participant, ParticipantState, ParticipantType, SearchFilter, SimUser} from "@models";
import {TeamTrainingSessionStateService} from "../../service/team-training-session-state.service";
import {SimConfigurationService, SimSessionService} from "@services";
import {Translations} from "@translations";
import {EnumLabel} from "../../../../../../../shared/shared-pipes/enum-label.pipe";

@Component({
	selector: "sim-team-training-session-step3",
	templateUrl: "./team-training-session-step3.component.html",
	styleUrl: "./team-training-session-step3.component.scss",
	standalone: false,
})
export class TeamTrainingSessionStep3Component implements OnInit {
	// Signals
	public displayParticipants = signal<Map<ParticipantType, Participant[]>>(
		new Map([
			[ParticipantType.Learner, []],
			[ParticipantType.Instructor, []],
			[ParticipantType.StandardizedPatient, []],
		]),
	);
	public selectedParticipant = signal<Participant>(null);
	public excludeIds = computed<string[]>(() => this._computeExcludeIds());
	public participantCount = computed<number>(() => this._computeParticipantCount());
	public userSearchFilters = computed<SearchFilter[] | SearchFilter>(() => {
		if (SimConfigurationService.settings.lmsIntegrationEnabled) {
			let courseId = this._stateService.event?.courseId;
			if (!!courseId) {
				return <SearchFilter>{
					searchColumn: "**courseId**",
					searchTerms: [courseId],
				};
			}
		}
		return [];
	});

	// Readonly
	protected readonly Participant: Participant;
	protected readonly ParticipantType = ParticipantType;
	protected readonly FetchDataType = FetchDataType;

	// Injections
	private _stateService = inject(TeamTrainingSessionStateService);
	private _sessionService = inject(SimSessionService);

	public ngOnInit() {
		this._stateService.session.participants.forEach((participant) =>
			this.displayParticipants().get(participant.participantType).push(participant),
		);
		this._initSelectedParticipant();
	}

	protected addParticipant(user: SimUser, participantType: ParticipantType): void {
		// Handle display state
		const newParticipant = {
			userId: user.id,
			participantType,
			state: ParticipantState.Registered,
			user,
		} as Participant;
		this._addToState(newParticipant);

		// Handle request
		const savePromise = this._sessionService.addParticipantToSession({
			sessionId: this._stateService.session.id,
			participantType,
			userId: user.id,
			generateAuthenticationCode: false,
		});
		this._stateService.autosavePromise = savePromise;
		savePromise.catch(() => {
			// Revert optimistic
			this._removeFromState(newParticipant);
		});
	}

	protected removeParticipant(participantToDelete: Participant): void {
		// Handle display state
		this._removeFromState(participantToDelete);

		// Handle request
		const savePromise = this._sessionService.removeParticipantFromSession({
			sessionId: this._stateService.session.id,
			userId: participantToDelete.userId,
			participantType: participantToDelete.participantType,
		});
		this._stateService.autosavePromise = savePromise;
		savePromise.catch(() => {
			// Revert optimistic
			this._addToState(participantToDelete);
		});
	}

	private _addToState(newParticipant: Participant): void {
		// Store
		this.displayParticipants.update((value) => {
			value.get(newParticipant.participantType).push(newParticipant);
			return new Map(value);
		});

		// Selected
		this.selectedParticipant.set(newParticipant);
	}

	private _removeFromState(participantToDelete: Participant): void {
		// Store
		const filteredParticipants = this.displayParticipants()
			.get(participantToDelete.participantType)
			.filter((participant) => participant.userId !== participantToDelete.userId);
		this.displayParticipants.update((value) => {
			value.set(participantToDelete.participantType, filteredParticipants);
			return new Map(value);
		});

		// Selected
		if (this.selectedParticipant()?.userId === participantToDelete.userId) {
			const newSelectedParticipant = filteredParticipants.length > 0 ? filteredParticipants.at(-1) : null;
			this.selectedParticipant.set(newSelectedParticipant);
		}
	}

	private _computeExcludeIds(): string[] {
		const result: string[] = [];
		this.displayParticipants().forEach((participants) => participants.forEach((participant) => result.push(participant.userId)));
		return result;
	}

	private _initSelectedParticipant(): void {
		const checkAndSelectParticipantByType = (participantType: ParticipantType) => {
			if (this.selectedParticipant()) {
				return;
			}
			const participants = this.displayParticipants().get(participantType);
			if (participants.length) {
				this.selectedParticipant.set(participants.at(0));
			}
		};
		checkAndSelectParticipantByType(ParticipantType.Instructor);
		checkAndSelectParticipantByType(ParticipantType.Learner);
		checkAndSelectParticipantByType(ParticipantType.StandardizedPatient);
	}

	private _computeParticipantCount(): number {
		let result = 0;
		this.displayParticipants().forEach((participants) => (result += participants.length));
		return result;
	}

	protected readonly T = Translations;
	protected readonly EnumLabel = EnumLabel;
}
